import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pin, Archive, Trash2, Users, User as UserIcon, GripVertical } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getColor } from './noteColors';

const NoteCard = ({
  note,
  onOpen,
  onTogglePin,
  onArchive,
  onDelete,
  onContextMenu,
  currentUserId,
  readOnly = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: note.id, disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const color = getColor(note.color);
  const isOwner = note.created_by === currentUserId;
  const isAssignee = note.assigned_to === currentUserId;
  const canEdit = isOwner || isAssignee;

  // For the card preview we render a plain-text excerpt (HTML stripped) so the
  // visual rhythm of the board stays tight. The full rich HTML is shown when
  // the note is opened.
  const plainPreview = (() => {
    const raw = note.content || '';
    if (!raw) return '';
    // Always strip tags via regex (never parse into a live DOM node just to
    // read .textContent — safer, and avoids any chance of an img/media tag
    // attempting to load while "detached").
    const text = /<\w+[\s>]/.test(raw) ? raw.replace(/<[^>]+>/g, ' ') : raw;
    return text.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 3).join(' • ');
  })();
  const updatedAgo = note.updated_at
    ? formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })
    : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`note-card-${note.id}`}
      className={`group relative rounded-lg border ${color.card} shadow-sm hover:shadow-md transition-all cursor-pointer break-inside-avoid mb-4`}
      onClick={() => onOpen && onOpen(note)}
      onContextMenu={(e) => onContextMenu && onContextMenu(e, note)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onOpen && onOpen(note, { edit: true });
      }}
    >
      {/* Drag handle (only for editable notes) */}
      {canEdit && !readOnly && (
        <button
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 left-2 opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
          data-testid={`note-drag-${note.id}`}
          title="Drag to reorder"
        >
          <GripVertical size={14} className={color.text} />
        </button>
      )}

      {/* Pin indicator */}
      {note.is_pinned && (
        <div className="absolute top-2 right-2">
          <Pin size={14} className={`${color.text} fill-current`} />
        </div>
      )}

      <div className="p-4 pl-8">
        {/* Title */}
        <h3 className={`font-semibold text-sm ${color.text} mb-1.5 line-clamp-2 pr-4`}>
          {note.title || 'Untitled'}
        </h3>

        {/* Content preview */}
        {plainPreview && (
          <p className={`text-xs ${color.text} opacity-80 line-clamp-3 mb-2`}>
            {plainPreview}
          </p>
        )}

        {/* Tags */}
        {Array.isArray(note.tags) && note.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {note.tags.slice(0, 4).map((t) => (
              <span
                key={t}
                className={`text-[10px] px-1.5 py-0.5 rounded-full bg-white/60 ${color.text} font-medium`}
              >
                #{t}
              </span>
            ))}
            {note.tags.length > 4 && (
              <span className={`text-[10px] ${color.text} opacity-60`}>
                +{note.tags.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={`flex items-center justify-between gap-2 text-[10px] ${color.text} opacity-70 mt-2`}>
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{note.category}</span>
            {note.is_shared && (
              <span className="flex items-center gap-0.5" title="Shared with team">
                <Users size={10} />
              </span>
            )}
            {note.assigned_to && (
              <span className="flex items-center gap-0.5" title="Assigned">
                <UserIcon size={10} />
              </span>
            )}
          </div>
          <span className="truncate">{updatedAgo}</span>
        </div>
      </div>

      {/* Action bar — visible on hover */}
      {canEdit && (
        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin && onTogglePin(note);
            }}
            className="p-1 rounded hover:bg-white/60"
            title={note.is_pinned ? 'Unpin' : 'Pin'}
            data-testid={`note-pin-${note.id}`}
          >
            <Pin size={12} className={color.text} />
          </button>
          {isOwner && !note.is_archived && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive && onArchive(note);
              }}
              className="p-1 rounded hover:bg-white/60"
              title="Archive"
              data-testid={`note-archive-${note.id}`}
            >
              <Archive size={12} className={color.text} />
            </button>
          )}
          {isOwner && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(note);
              }}
              className="p-1 rounded hover:bg-white/60"
              title="Delete"
              data-testid={`note-delete-${note.id}`}
            >
              <Trash2 size={12} className={color.text} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NoteCard;
